
/**
 * Temporary side-by-side comparison page — shows the new primary_logo.svg
 * in every surface that actually renders it (sidebar header, login option B,
 * top header bar). Mounted at /primary-preview. Delete this file + the
 * App.js route entry once a final pick is confirmed.
 */

const PRIMARY = '/brand/primary_logo.svg';
const ICON = '/brand/icon_mark.svg';

function Tile({ tag, name, w = 360, h = 90, bg = '#161618', children, note }) {
  return (
    <div className="rounded-lg overflow-hidden border border-border bg-[#0F0F11]">
      <div className="px-3 py-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-card">
        <span className="font-mono">{tag}</span>
        <span className="flex-1 text-muted-foreground truncate">{name}</span>
      </div>
      <div className="relative" style={{ width: w, height: h, background: bg }}>
        {children}
      </div>
      {note && <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">{note}</div>}
    </div>
  );
}

export default function PrimaryPreviewPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-foreground py-10 px-12" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="max-w-6xl mx-auto mb-10">
        <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', fontSize: 11, color: '#71717A' }}>
          New primary_logo.svg · live preview
        </div>
        <h1 className="text-3xl font-bold mt-1">Where this logo actually shows up</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
          Every surface below renders the new SVG at its production size — what you see is what users will see.
        </p>
      </header>

      <main className="max-w-6xl mx-auto space-y-10">
        <section>
          <h2 className="text-sm uppercase tracking-widest text-[#4575A8] mb-3 font-mono">01 · Sidebar header (expanded · 240px wide)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Tile tag="A" name="Current production size — 28px height" note="What it looks like in the actual app sidebar today.">
              <div className="absolute inset-0 flex items-center px-6">
                <img src={PRIMARY} alt="Strata × Framework" style={{ height: 28, width: 'auto' }} />
              </div>
            </Tile>
            <Tile tag="B" name="Bigger — 40px height" note="With more breathing room. Your call which feels right.">
              <div className="absolute inset-0 flex items-center px-6">
                <img src={PRIMARY} alt="Strata × Framework" style={{ height: 40, width: 'auto' }} />
              </div>
            </Tile>
          </div>
        </section>

        <section>
          <h2 className="text-sm uppercase tracking-widest text-[#4575A8] mb-3 font-mono">02 · Top header bar (current placement)</h2>
          <Tile tag="" name="Top page header bar — currently 22px high" w={1200} h={64} note="If you said 'b' (keep + bump) earlier, we'd render at ~32px.">
            <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-6 border-b border-border">
              <img src={PRIMARY} alt="" style={{ height: 24, width: 'auto' }} />
              <div className="text-[12px] text-muted-foreground">Page Title · Breadcrumb · etc.</div>
            </div>
          </Tile>
        </section>

        <section>
          <h2 className="text-sm uppercase tracking-widest text-[#4575A8] mb-3 font-mono">03 · Login screen — Option B (Minimalist Horizon)</h2>
          <div className="rounded-xl overflow-hidden border border-border mx-auto"
               style={{ width: 420, height: 480, background: '#161618',
                        backgroundImage: 'radial-gradient(ellipse at top, rgba(97,136,196,0.07), transparent 60%)' }}>
            <div className="px-8 pt-10">
              <img src={PRIMARY} alt="Strata × Framework" style={{ height: 44, width: 'auto' }} />
            </div>
            <div className="px-8 mt-6 text-[11px] uppercase tracking-widest text-muted-foreground">Sign in to your workspace</div>
            <div className="px-8 mt-6 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</div>
              <div className="h-9 rounded bg-[#0F0F11] border border-border" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Password</div>
              <div className="h-9 rounded bg-[#0F0F11] border border-border" />
              <div className="h-9 rounded bg-[#4575A8] mt-3 flex items-center justify-center text-[12px] text-[#161618] font-semibold tracking-wide uppercase">Sign In</div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-sm uppercase tracking-widest text-[#4575A8] mb-3 font-mono">04 · Crisp text comparison @ varying sizes</h2>
          <div className="bg-card border border-border rounded-lg p-8 space-y-6">
            {[16, 24, 32, 48, 64].map(h => (
              <div key={h} className="flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground w-12 font-mono">{h}px</span>
                <img src={PRIMARY} alt="" style={{ height: h, width: 'auto' }} />
              </div>
            ))}
          </div>
        </section>

        <footer className="pt-6 border-t border-border text-[11px] text-muted-foreground">
          Reply with your call: keep this primary, or want me to swap something specific.
        </footer>
      </main>
    </div>
  );
}
